import React, {useState, useCallback, useEffect, useRef} from 'react';
import {
  getContainerStyle,
  getItemStyle,
  getSliderStyle,
} from './SegmentedButton.config.web';
import {styles} from './styles.web';
import {SegmentedButtonProps} from './SegmentedButton.types.web';
import {useTheme} from '../../../../utils/theming/themeProvider';

const SegmentedButton = (
  props: React.PropsWithChildren<SegmentedButtonProps>,
) => {
  const {
    id,
    className,
    items,
    selectedId,
    size,
    selectedColor,
    animationDuration,
    containerStyle,
    onChange,
  } = props;

  const {colors, fontStyles} = useTheme();
  const [optionChecked, setOptionChecked] = useState(
    selectedId || items ? items[0]?.id : undefined,
  );
  const containerWidthRef = useRef(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [sliderOpacity, setSliderOpacity] = useState(0);

  const runAnimation = useCallback(
    (index: number, itemId: string) => {
      setOptionChecked(itemId);
      const itemCount = items.length;
      const destPos =
        (index / itemCount) *
        (containerWidthRef.current -
          (itemCount === 2 && size !== 'small'
            ? 16
            : size === 'small' && itemCount !== 3
            ? 4
            : size === 'small'
            ? 4
            : 16));
      setSliderValue(destPos);
      setSliderOpacity(1);

      setTimeout(() => {
        onChange(items[index]);
      }, animationDuration);
    },
    [items, animationDuration, onChange],
  );

  useEffect(() => {
    const index = items.findIndex(item => item.id === selectedId);
    if (index >= 0) runAnimation(index, selectedId);
  }, [selectedId, runAnimation]);

  const containerGeneralStyle = getContainerStyle(colors, size);
  const sliderStyle = getSliderStyle(
    colors,
    size,
    selectedColor,
    items.length,
    sliderValue,
    sliderOpacity,
  );

  const generalStyles = styles(colors);
  const fontStyle =
    size === 'medium' ? fontStyles.buttonMD : fontStyles.buttonSM;
  const activeTextStyle =
    selectedColor === 'purple' ? colors.neutral100 : colors.neutral1000;

  return (
    <div
      id={id}
      className={className}
      style={{
        ...generalStyles.container,
        ...containerGeneralStyle,
        ...containerStyle,
      }}
      ref={ref => {
        if (ref) {
          const width = ref.getBoundingClientRect().width;
          containerWidthRef.current = width;
        }
      }}>
      <div style={{...generalStyles.slider, ...sliderStyle}} />
      {items.map((item, index) => (
        <div
          key={item.id}
          style={{
            ...generalStyles.item,
            ...getItemStyle(items.length, size),
          }}
          onClick={() => runAnimation(index, item.id)}>
          <span
            style={{
              ...generalStyles.itemText,
              color:
                optionChecked === item.id
                  ? activeTextStyle
                  : colors.neutral1000,
              ...fontStyle,
              lineHeight: fontStyle?.lineHeight + 'px',
            }}>
            {item.text}
          </span>
        </div>
      ))}
    </div>
  );
};

export default SegmentedButton;
